export type FirebaseConfig = {
    apiKey: string
    authDomain: string
    projectId: string
    appId: string
}

export type AppConfig = {
    apiPrefix: string
    authenticatedEntryPath: string
    unAuthenticatedEntryPath: string
    locale: string
    accessTokenPersistStrategy: 'localStorage' | 'sessionStorage' | 'cookies'
    googleClientId: string
    firebase: FirebaseConfig
    firebaseEnabled: boolean
    salesEmail: string
}

const appConfig: AppConfig = {
    /**
     * Never inline an API host (CLAUDE.md §9). In dev this is empty and Vite
     * proxies /api to the Django container; in prod it is the api.trackheros.com
     * origin baked in at build time by VITE_API_BASE_URL.
     */
    apiPrefix: `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1`,
    /**
     * The dashboard, not Analytics. The first questions after signing in are
     * "what am I in today, did the bot get the last one, and what did I commit
     * to" — the dashboard answers all three at a glance and links through to
     * the full pages. Analytics stays something you go and look at
     * deliberately.
     *
     * This is also what makes Dashboard the nav item selected by default: `/`
     * redirects here, and the highlight follows the route's key.
     */
    authenticatedEntryPath: '/dashboard',
    unAuthenticatedEntryPath: '/sign-in',
    locale: 'en',
    /**
     * Where the JWT pair lives.
     *
     * localStorage is the deliberate choice over cookies here: the API is on a
     * different origin, so a cookie would have to be SameSite=None and would
     * be sent on every cross-site request anyway. Neither option survives XSS
     * — that is the cost of bearer tokens, and it is why the access token's
     * lifetime is short and refresh tokens rotate (backend SIMPLE_JWT).
     */
    accessTokenPersistStrategy: 'localStorage',
    /**
     * The Google *sign-in* client id — not the one that connects a client's
     * calendar (backend GOOGLE_OAUTH_CLIENT_ID). Public by design: the browser
     * hands it to Google, and the security is the backend's signature and
     * audience check on the token that comes back.
     *
     * Empty means the Google button is not rendered at all, which is what a
     * dev machine without the variable set should see — a button that fails on
     * click is worse than no button.
     */
    googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '',
    /**
     * Firebase Authentication.
     *
     * All four values are public by design — they identify the project to
     * Google and authorise nothing. What protects an account is the backend
     * verifying the signature and audience of the ID token that comes back, so
     * `projectId` here must equal the backend's `FIREBASE_PROJECT_ID` or every
     * sign-in fails that check.
     *
     * Firebase is an identity provider only: we exchange its ID token at
     * `/auth/firebase` for our own JWT pair, and no application data ever
     * passes through it.
     */
    firebase: {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
        appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
    },
    /**
     * Whether to offer Firebase sign-in at all.
     *
     * Empty config means a dev machine with no Firebase project, and the right
     * behaviour there is to hide the social buttons rather than render ones
     * that throw on click — the same call `googleClientId` already makes.
     */
    get firebaseEnabled() {
        return Boolean(this.firebase.apiKey && this.firebase.projectId)
    },
    /**
     * Where the plan picker's Enterprise row sends people. Enterprise is
     * sales-led — an annual contract, not a checkout — so it is an email, the
     * same address the website's pricing page uses (`website/src/lib/legal.ts`).
     * One contact address everywhere, sales included (2026-10-15).
     */
    salesEmail: 'admin@trackheros.com',
}

export default appConfig
